import { useMemo } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import PeerDelta from './PeerDelta.js';

export const REASON_LABEL = {
  COLOR_CHANGE: 'Color Change',
  COLOR_CHANGE_ON_FLY: 'Color Change (on the fly)',
  PRODUCT_CHANGEOVER: 'Product Changeover',
  PHASE_CHANGEOVER: 'Phase Changeover',
  DRUM_CHANGE: 'Drum Change',
  CLEAN_UP: 'Clean Up',
  UNWINDING: 'Unwinding',
  DOWNTIME: 'Breakdown',
  IN_DRUM_STOP: 'In-Drum Stop',
  IDLE: 'Idle',
  COLD_START: 'Cold Start'
};

export const REASON_COLOR = {
  COLOR_CHANGE: '#f59e0b',
  COLOR_CHANGE_ON_FLY: '#fcd34d',
  PRODUCT_CHANGEOVER: '#ef4444',
  PHASE_CHANGEOVER: '#6366f1',
  DRUM_CHANGE: '#10b981',
  CLEAN_UP: '#06b6d4',
  UNWINDING: '#a855f7',
  DOWNTIME: '#dc2626',
  IN_DRUM_STOP: '#94a3b8',
  IDLE: '#64748b',
  COLD_START: '#0ea5e9'
};

const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Inner Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Outer Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

const fmtDur = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const columns = [
  {
    accessorKey: 'operator',
    header: 'Operator',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold text-900">{original.operator}</span>
    ),
    meta: { headerProps: { style: { width: '13%' } }, cellProps: { className: 'py-2' } }
  },
  {
    accessorKey: 'reason',
    header: 'Reason',
    cell: ({ row: { original } }) => (
      <Badge
        className="rounded-pill fs-10"
        style={{ backgroundColor: REASON_COLOR[original.reason] || '#6b7280' }}
      >
        {REASON_LABEL[original.reason] || original.reason}
      </Badge>
    ),
    meta: { headerProps: { style: { width: '15%' } } }
  },
  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <span>{original.productLabel || <span className="text-700">—</span>}</span>
    ),
    meta: { headerProps: { style: { width: '15%' } } }
  },
  {
    accessorKey: 'phase',
    header: 'Phase',
    cell: ({ row: { original } }) => (
      <span className="text-700">{PHASE_LABEL[original.phase] || original.phase || '—'}</span>
    ),
    meta: { headerProps: { style: { width: '12%' } } }
  },
  {
    accessorKey: 'machineId',
    header: 'Machine',
    cell: ({ row: { original } }) => (
      <span className="text-uppercase text-700 fs-10">{original.machineId || '—'}</span>
    ),
    meta: { headerProps: { style: { width: '8%' } } }
  },
  {
    accessorKey: 'events',
    header: 'Events',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column text-end">
        <span className="fw-semibold">{original.events}</span>
        {original.measuredEvents < original.events ? (
          <span
            className="fs-10 text-warning"
            title="Some of these were never touched by anyone — recorded, but kept out of the median"
          >
            {original.events - original.measuredEvents} unmeasured
          </span>
        ) : null}
      </div>
    ),
    meta: {
      headerProps: { style: { width: '8%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'medianCostSec',
    header: 'Median',
    cell: ({ row: { original } }) => (
      <span className="fw-bold text-warning">
        {original.medianCostSec != null ? fmtDur(original.medianCostSec) : '—'}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '9%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    id: 'peer',
    accessorFn: r => r.deltaPct ?? 9999,
    header: 'vs peers',
    cell: ({ row: { original } }) => (
      <PeerDelta
        delta={original.deltaPct}
        peers={original.peerOperators}
        peerValue={
          original.peerMedianCostSec != null ? fmtDur(original.peerMedianCostSec) : null
        }
        // less downtime is better, so below the group is the good direction
        higherIsBetter={false}
      />
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'totalCostSec',
    header: 'Total lost',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column text-end">
        <span className="fw-semibold">{fmtDur(original.totalCostSec)}</span>
        {original.totalIdleSec > 0 ? (
          <span className="fs-10 text-600" title="Machine stopped with nobody working on it">
            +{fmtDur(original.totalIdleSec)} idle
          </span>
        ) : null}
      </div>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  }
];

/**
 * Downtime broken out by operator, product, phase and reason — with each row
 * measured against the other operators who hit the same situation.
 *
 * "Ionescu loses 20 minutes to changeovers" says nothing until you know it is on
 * a product where everyone else loses 25.
 */
const DowntimeMatrixTable = ({ rows = [], isLoading }) => {
  const data = useMemo(() => rows, [rows]);

  const table = useAdvanceTable({
    data,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 gap-2">
        <Spinner animation="border" size="sm" variant="primary" />
        <span className="text-700 fs-9">Loading…</span>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="text-center py-5 text-700 fs-9">
        No downtime recorded for these filters yet.
      </div>
    );
  }

  return (
    <AdvanceTableProvider {...table}>
      <AdvanceTable tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }} />
      <AdvanceTableFooter pagination className="py-3" />
    </AdvanceTableProvider>
  );
};

export default DowntimeMatrixTable;
